import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { ArrowLeft, Home, ChevronDown } from "lucide-react";
import { getOrCreateSessionId } from "../utils/session";

export default function CustomizePage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // ✅ access previous state
  const [product, setProduct] = useState(location.state?.product || null); 
  const [formData, setFormData] = useState(location.state?.formData || {});
  const [selectedColor, setSelectedColor] = useState(location.state?.selectedColor || "");
  const [quantity, setQuantity] = useState(location.state?.quantity || 1);
  const [sliderRef] = useKeenSlider({ loop: false });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  // --- Fetch product only if not passed via state ---
  useEffect(() => {
    if (product) return; // ✅ skip if we have state

    const fetchProduct = async () => {
      try {
        const sessionId = getOrCreateSessionId();
        const res = await api.get(`/cproduct/${productId}`, { params: { session_id: sessionId } });
        const prod = res.data;
        setProduct(prod);

        const initialForm = {};
        prod.customization_schema?.forEach((field) => {
          initialForm[field.label] = field.type === "checkbox" ? [] : "";
        });

        if (prod.color?.length > 0) {
          setSelectedColor(prod.color[0]);
          initialForm["Color"] = prod.color[0];
        }

        setFormData(initialForm);
      } catch (err) {
        console.error("Failed to fetch product:", err);
        setError("Product not found or failed to load.");
      }
    };

    fetchProduct();
  }, [productId, product]);

  const handleChange = (label, value, type) => {
    setFormData((prev) => {
      if (type === "checkbox") {
        const updated = new Set(prev[label]);
        updated.has(value) ? updated.delete(value) : updated.add(value);
        return { ...prev, [label]: [...updated] };
      }

      if (label.toLowerCase() === "color") {
        setSelectedColor(value);
      }

      return { ...prev, [label]: value };
    });
  };

  const handleSubmit = () => {
    const newErrors = {};
    product.customization_schema?.forEach((field) => {
      const value = formData[field.label];
      if ((field.type === "checkbox" && (!value || value.length === 0)) || (field.type !== "checkbox" && !value)) {
        newErrors[field.label] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    navigate(`/checkout/${productId}`, { state: { customization: formData, quantity, product } });
  };

  // --- Images helpers ---
  const getImagesForColor = (imageMap, color) => {
    if (!imageMap || !color) return [];
    if (Array.isArray(imageMap)) {
      const entry = imageMap.find((e) => e.color?.toLowerCase() === color.toLowerCase());
      return entry?.urls || [];
    }
    if (typeof imageMap === "object") {
      return imageMap[color.toLowerCase()] || [];
    }
    return [];
  };

  const getMainImage = () => {
    const images = getImagesForColor(product?.imageUrl, selectedColor);
    return images[0] || "";
  };

  const allImages = useMemo(() => {
    if (!product) return [];
    const images = [];
    const main = getMainImage();
    if (main) images.push(main);

    if (product.imageUrl) {
      if (Array.isArray(product.imageUrl)) {
        product.imageUrl.forEach((entry) => {
          entry.urls.forEach((url) => {
            if (url && url !== main) images.push(url);
          });
        });
      } else {
        Object.values(product.imageUrl).forEach((urls) => {
          if (Array.isArray(urls)) urls.forEach((url) => { if (url && url !== main) images.push(url); });
          else if (typeof urls === "string" && urls !== main) images.push(urls);
        });
      }
    }
    return images;
  }, [product, selectedColor]);

  let unitPrice = 0;
  if (Array.isArray(product?.price) && product.price.length > 0) unitPrice = Number(product.price[0]) || 0;
  else if (typeof product?.price === "number") unitPrice = product.price;

  const totalPrice = useMemo(() => unitPrice * quantity, [unitPrice, quantity]);

  if (error) return <div className="p-6 text-red-600 text-center">{error}</div>;
  if (!product) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Back + Home */}
      <div className="relative flex items-center mb-4">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-black">
          <ArrowLeft className="w-5 h-5 mr-2" />
        </button>
        <button onClick={() => navigate("/")} className="absolute left-1/2 transform -translate-x-1/2 text-gray-600 hover:text-black">
          <Home className="w-5 h-5" />
        </button>
      </div>

      

      {/* Image Slider */}
      <div ref={sliderRef} className="keen-slider mb-6 rounded-lg overflow-hidden">
        {allImages.map((url, idx) => (
          <div key={idx} className="keen-slider__slide">
            <div className="w-full aspect-[2/3] md:min-h-[350px] overflow-hidden rounded-lg">
              <img src={url} alt={`Slide ${idx}`} className="w-full h-full object-cover" />
            </div>
          </div>
        ))}
      </div>

<p className="mb-2">{product.name}</p>

      {/* Price & Quantity */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-semibold">Unit Price: ₹{unitPrice}</p>
          <p className="text-gray-600 text-sm">Total: ₹{totalPrice}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="px-3 py-1 bg-gray-300 rounded-full" onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
          <span className="min-w-[2rem] text-center">{quantity}</span>
          <button className="px-3 py-1 bg-gray-300 rounded-full" onClick={() => setQuantity(q => q + 1)}>+</button>
        </div>
      </div>

      {/* Customization Form */}
      {product.customization_schema?.map((field, idx) => (
        <div key={idx} className="mb-4">
          <label className="block font-medium mb-1">{field.label}</label>

          {field.type === "select" && Array.isArray(field.options) && (
            <div className="relative">
              <select
                className="w-full p-2 border rounded pr-10 appearance-none bg-white"
                value={formData[field.label]}
                onChange={(e) => handleChange(field.label, e.target.value, field.type)}
              >
                <option value="">Select</option>
                {field.options.map((opt, i) => {
                  const optionValue = typeof opt === "string" ? opt : opt.label;
                  return <option key={i} value={optionValue}>{optionValue}</option>;
                })}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          )}

          {field.type === "text" && (
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder={field.placeholder || ""}
              value={formData[field.label]}
              onChange={(e) => handleChange(field.label, e.target.value, field.type)}
            />
          )}

          {field.type === "textarea" && (
            <textarea
              className="w-full p-2 border rounded"
              placeholder={field.placeholder || ""}
              value={formData[field.label]}
              onChange={(e) => handleChange(field.label, e.target.value, field.type)}
            />
          )}

          {field.type === "checkbox" && field.options.map((opt, i) => {
            const optLabel = typeof opt === "string" ? opt : opt.label;
            return (
              <label key={i} className="block">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={formData[field.label]?.includes(optLabel)}
                  onChange={() => handleChange(field.label, optLabel, field.type)}
                />
                {optLabel}
              </label>
            );
          })}

          {errors[field.label] && <p className="text-red-500 text-sm mt-1">{errors[field.label]}</p>}
        </div>
      ))}

      <button onClick={handleSubmit} className="mt-4 px-6 py-2 bg-black text-white rounded-full">
        Proceed to Checkout
      </button>
    </div>
  );
}
